import { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useFetchWishlists } from '../../../hooks/wishlists/useFetchWishlists';
import { useCreateWishListStudio } from '../../../hooks/wishlists/useCreateWishListStudio';
import { useCreateWishlist } from '../../../hooks/wishlists/useCreateWishlist';
import { getCloudFrontImageUrl } from '../../../util/cloudFrontUtils';
import { trackWishlistAdded } from '../../../util/tracking';

import classes from './FavouritePhone.module.css';  

const FavouritePhone = ({ 
  showFavouriteModal, 
  onClose,
  setSelectedWishlist,
  studioId,
  backdropClassName,
  backdropVisibleClassName,
}) => {
    const [isNewCollection, setIsNewCollection] = useState(false);
    const [newCollectionName, setNewCollectionName] = useState('');
    const [isClosing, setIsClosing] = useState(false);
    const [favouritesCreated, setFavouritesCreated] = useState(false);
    const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
    const creationAttempted = useRef(false);
    const inputRef = useRef(null);

    const dismissModal = useCallback(() => {
        setIsNewCollection(false);
        setNewCollectionName('');
        onClose?.();
    }, [onClose]);
    
  const { user } = useSelector((state) => state?.auth);
  const { id: userID } = user;
  
  const { wishlistsData, isLoading, error } = useFetchWishlists(userID);
  const { handleWishlistStudioCreation, loading: createStudioLoading } = useCreateWishListStudio();
  const { handleWishlistCreation, loading: createWishlistLoading } = useCreateWishlist();

  // Check if Favourites wishlist exists and create it if it doesn't
  useEffect(() => {
    const createFavouritesWishlist = async () => {
      // Prevent multiple creation attempts
      if (creationAttempted.current) return;
      
      if (!isLoading && wishlistsData?.items) {
        const favouritesExists = wishlistsData.items.some(wishlist => 
          wishlist.name === 'Favoriten'
        );
        
        if (!favouritesExists) {
          creationAttempted.current = true;
          try {
            await handleWishlistCreation({
              name: 'Favoriten',
              userID: userID
            });
            setFavouritesCreated(true);
          } catch (error) {
            console.error('Error creating Favoriten wishlist:', error);
            // Reset the flag on error so we can try again
            creationAttempted.current = false;
          }
        } else {
          setFavouritesCreated(true);
          creationAttempted.current = true;
        }
      }
    };

    createFavouritesWishlist();
  }, [wishlistsData, handleWishlistCreation, userID, isLoading]);

  // Handle viewport height changes (Safari navbar show/hide)
  useEffect(() => {
    const handleResize = () => {
      setViewportHeight(window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    
    // Also listen for visual viewport changes (Safari specific)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      }
    };
  }, []);

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            dismissModal();
        }, 300);
    };

    const handleBackdropClick = () => {
        dismissModal();
    };

    useEffect(() => {
        if (!showFavouriteModal) {
            setIsClosing(false);
        }
    }, [showFavouriteModal]);

    // Focus the input when the new collection view opens
    useEffect(() => {
        if (isNewCollection && showFavouriteModal && inputRef.current) {
            // Delay to ensure element is painted before focusing
            const id = requestAnimationFrame(() => {
                try {
                    inputRef.current.focus({ preventScroll: false });
                } catch (e) {
                    // noop
                }
            });
            return () => cancelAnimationFrame(id);
        }
    }, [isNewCollection, showFavouriteModal]);

  const processStudioImage = (imageUrl) => {
    if (!imageUrl) return '';
    return getCloudFrontImageUrl(imageUrl, {
        width: 1200,
        height: 800,
        fit: 'cover'
    });
  };

  const handleWishlistClick = async (wishlistName, wishlistId) => {
    try {
      await handleWishlistStudioCreation({
        wishListID: wishlistId,
        studioID: studioId
      });
      trackWishlistAdded({
        studioId,
        source: 'studio_profile',
      });
      setSelectedWishlist(wishlistName);
      dismissModal();
    } catch (err) {
      console.error('Error adding to wishlist:', err);
    }
    };

    const handleNewCollection = () => {
        setIsNewCollection(true);
    };

    const handleCancel = () => {
        setIsNewCollection(false);
    setNewCollectionName('');
    };
    
    const handleNewCollectionBodyPointerDown = (e) => {
        if (inputRef.current && e.target && !inputRef.current.contains(e.target)) {
            try {
                inputRef.current.blur();
            } catch (_) {
                // noop
            }
        }
    };

  const handleSave = async () => {
    if (newCollectionName.trim()) {
      try {
        // First create the wishlist
        const newWishlist = await handleWishlistCreation({
          name: newCollectionName,
          userID: userID
        });

        // Then add the studio to it
        if (newWishlist?.id) {
          await handleWishlistStudioCreation({
            wishListID: newWishlist.id,
            studioID: studioId
          });
        }

        trackWishlistAdded({
          studioId,
          source: 'studio_profile',
        });
        setSelectedWishlist(newCollectionName);
        setIsNewCollection(false);
        setNewCollectionName('');
        dismissModal();
      } catch (err) {
        console.error('Error creating new wishlist:', err);
      }
    }
  };

  if (error) return <div>Favoritenlisten konnten nicht geladen werden.</div>;

    return (
    <>
      {backdropClassName && (
        <div
          className={`${backdropClassName} ${backdropVisibleClassName ?? ''}`}
          onClick={handleBackdropClick}
          aria-hidden
        />
      )}
    <div 
      className={`${classes.favouritePhoneContainer} ${isClosing ? classes.hidden : ''}`}
      style={{
        height: `${viewportHeight - 44 - 34}px`, // Dynamic height based on current viewport
        maxHeight: `${viewportHeight - 44}px` // Ensure it doesn't exceed viewport
      }}
    >
        <div className={classes.favouritePhoneHeader}>
            <h2 className='titleSemibold3'>{isNewCollection ? 'Neue Kollektion' : 'Zur Kollektion'}</h2>
            <button className={classes.closeButton} onClick={handleClose}>
                <svg xmlns="http://www.w3.org/2000/svg" width="17" height="18" viewBox="0 0 17 18" fill="none">
                    <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 9L0 1.49054L0.990535 0.5L8.5 8.00947L16.0095 0.5L17 1.49054L9.49054 9L17 16.5095L16.0095 17.5L8.5 9.99054L0.990535 17.5L0 16.5095L7.50947 9Z" fill="#1F1F1F"/>
                </svg>
            </button>
        </div>
        <div className={classes.favouriteModalBodyContainer}>
          {!isNewCollection ? (
          <div className={classes.favouriteModalBody}>
            {isLoading || createStudioLoading || createWishlistLoading ? (
              <div className={classes.wishlistsContainer}>
                <a className="titleSemibold3">Wird geladen...</a>
              </div>
            ) : (
              <div className={classes.wishlistsContainer}>
                <div className={classes.wishlistContainer}>
                  <div 
                    onClick={handleNewCollection} 
                    className={classes.wishlistInnerContainer}
                  >
                    <div className={classes.newWishlistIcon}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M17 9.5317H1V8.45539H17V9.5317Z" fill="#1F1F1F"/>
                        <path fillRule="evenodd" clipRule="evenodd" d="M9.53433 1V17H8.45801V1H9.53433Z" fill="#1F1F1F"/>
                        <path fillRule="evenodd" clipRule="evenodd" d="M17 9.5317H1V8.45539H17V9.5317Z" stroke="black" strokeWidth="0.1" strokeMiterlimit="10"/>
                        <path fillRule="evenodd" clipRule="evenodd" d="M9.53433 1V17H8.45801V1H9.53433Z" stroke="black" strokeWidth="0.1" strokeMiterlimit="10"/>
                      </svg>
                    </div>
                  </div>
                  <h2 className='titleSemibold3'>Neue Kollektion</h2>
                </div>
                {wishlistsData?.items && wishlistsData.items.length > 0 && wishlistsData.items.map((wishlist) => (
                  <div 
                    className={classes.wishlistContainer} 
                    key={wishlist.id}
                    onClick={() => handleWishlistClick(wishlist.name, wishlist.id)}
                  >
                    <div className={classes.favouriteFolderImages}>
                      {!wishlist.wishListStudios?.items?.length ? (
                        <div className={classes.emptyContainer}>
                          <svg className={classes.svgMobile} xmlns="http://www.w3.org/2000/svg" width="166" height="122" viewBox="0 0 166 122" fill="none">
                            <rect width="165.965" height="121.708" rx="12" fill="#F0F1F4"/>
                            <path d="M117.058 45.5919L111.607 85.9019C111.297 88.1219 109.268 89.6819 107.048 89.3719L99.4375 88.4019C99.4375 88.3019 99.4475 88.2019 99.4475 88.1019V39.1719L113.577 41.0219C115.797 41.3219 117.358 43.3619 117.058 45.5819V45.5919Z" fill="white"/>
                            <path d="M48.0369 45.5919L53.4869 85.9019C53.7969 88.1219 55.8269 89.6819 58.0469 89.3719L65.6569 88.4019C65.6569 88.3019 65.6469 88.2019 65.6469 88.1019V39.1719L51.5169 41.0219C49.2969 41.3219 47.7369 43.3619 48.0369 45.5819V45.5919Z" fill="white"/>
                            <path d="M95.3965 29C98.1917 29.0001 100.446 31.2708 100.446 34.0596V88.1201C100.446 88.212 100.447 88.3618 100.432 88.5195L100.431 88.5186C100.23 91.124 98.0565 93.1796 95.3965 93.1797H69.7061C67.0367 93.1796 64.856 91.1096 64.6689 88.4912L64.666 88.4561V88.4199C64.666 88.3991 64.6648 88.3742 64.6621 88.3203C64.6598 88.2745 64.6563 88.1992 64.6562 88.1201V34.0596C64.6565 31.2708 66.9108 29.0001 69.7061 29H95.3965Z" fill="white" stroke="#F0F1F4" strokeWidth="2"/>
                            <path d="M93.9064 76.1719H70.2964C69.479 76.1719 68.8164 76.8345 68.8164 77.6519C68.8164 78.4693 69.479 79.1319 70.2964 79.1319H93.9064C94.7238 79.1319 95.3864 78.4693 95.3864 77.6519C95.3864 76.8345 94.7238 76.1719 93.9064 76.1719Z" fill="#F0F1F4"/>
                            <path d="M80.0664 81.8203H70.2964C69.479 81.8203 68.8164 82.4829 68.8164 83.3003C68.8164 84.1177 69.479 84.7803 70.2964 84.7803H80.0664C80.8838 84.7803 81.5464 84.1177 81.5464 83.3003C81.5464 82.4829 80.8838 81.8203 80.0664 81.8203Z" fill="#F0F1F4"/>
                          </svg>
                        </div>
                      ) : wishlist.wishListStudios.items.length === 1 || wishlist.wishListStudios.items.length === 2 ? (
                        <div className={classes.singleImageContainer}>
                          <img 
                            src={processStudioImage(wishlist.wishListStudios.items[0].studio.images[0])} 
                            alt={wishlist.wishListStudios.items[0].studio.StudioName} 
                          />
                        </div>
                      ) : (
                        <>
                          <div className={classes.leftContainer}>
                            <img 
                              src={processStudioImage(wishlist.wishListStudios.items[0].studio.images[0])} 
                              alt={wishlist.wishListStudios.items[0].studio.StudioName} 
                            />
                          </div>
                          <div className={classes.rightContainer}>
                            <div className={classes.topContainer}>
                              <img 
                                src={processStudioImage(wishlist.wishListStudios.items[1].studio.images[0])} 
                                alt={wishlist.wishListStudios.items[1].studio.StudioName} 
                              />
                            </div>
                            <div className={classes.bottomContainer}>
                              {wishlist.wishListStudios.items.length >= 3 && (
                                <img 
                                  src={processStudioImage(wishlist.wishListStudios.items[2].studio.images[0])} 
                                  alt={wishlist.wishListStudios.items[2].studio.StudioName} 
                                />
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                    <div className={classes.wishlistInfoContainer}>
                      <h2 className='titleSemibold3'>{wishlist.name}</h2>
                      <p className='regularLarge'>{wishlist.wishListStudios?.items?.length || 0} Studios</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
           ) : (
            <div 
              className={classes.newCollectionBody}
              onMouseDown={handleNewCollectionBodyPointerDown}
              onTouchStart={handleNewCollectionBodyPointerDown}
            >
              <div className={classes.inputContainer}>
                <label className='semiboldSmall' htmlFor="collectionName">Name</label>
                <input 
                  className='regularLarge' 
                  type="text" 
                  placeholder='Name eingeben'
                  id="collectionName"
                  ref={inputRef}
                  autoFocus
                  inputMode="text"
                  onClick={() => inputRef.current && inputRef.current.focus()}
                  value={newCollectionName}
                  onChange={(e) => setNewCollectionName(e.target.value)}
                />
              </div>
              <div className={classes.buttonContainer}>
                <button 
                  className={`mediumLarge ${classes.saveButton} ${classes.button}`} 
                  onClick={handleCancel}
                >
                  Abbrechen
                </button>
                <button 
                  className={`mediumLarge ${classes.cancelButton} ${classes.button}`} 
                  onClick={handleSave}
                >
                  Speichern
                </button>
              </div>
            </div>
          )}
        </div>
    </div>
    </>
  );
};

export default FavouritePhone;
